import React, { useState, useEffect } from 'react';
import { Layout, Form, Input, Button, message, Spin, notification } from 'antd';
import { LockOutlined, CheckCircleOutlined, LaptopOutlined } from '@ant-design/icons';
import NavigationBar from '../../components/NavigationBar';
import DOTheme from '../../components/directOrders/doTheme';
import { useAuth } from '../../context/AuthContext';
import { authService } from '../../services/authService';
import laptopService from '../../services/laptopService';
import { roleName } from '../../utils/roles';
import '../../styles/account.css';

const MyAccount = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  // My laptop state
  const [laptop, setLaptop] = useState(null);
  const [laptopNotice, setLaptopNotice] = useState(null);
  const [laptopLoading, setLaptopLoading] = useState(true);
  const [laptopSaving, setLaptopSaving] = useState(false);
  const [laptopForm] = Form.useForm();

  useEffect(() => {
    fetchMyLaptop();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMyLaptop = async () => {
    setLaptopLoading(true);
    try {
      const { laptop: myLaptop, notFoundMessage } = await laptopService.getMyLaptop();
      setLaptop(myLaptop);
      setLaptopNotice(notFoundMessage);
      if (myLaptop) {
        laptopForm.setFieldsValue({ inventoryNumber: myLaptop.inventoryNumber });
      }
    } catch (error) {
      setLaptop(null);
      setLaptopNotice(error.message || 'Failed to load laptop information');
    } finally {
      setLaptopLoading(false);
    }
  };

  const handleSaveMyLaptop = async (values) => {
    setLaptopSaving(true);
    try {
      const updated = await laptopService.updateMyLaptop(values.inventoryNumber);
      setLaptop(updated);
      message.success('Inventory number updated and confirmed!');
    } catch (error) {
      if (error.message.includes('already been confirmed')) {
        message.info('This was already confirmed — refreshing.');
        fetchMyLaptop();
      } else if (error.message.includes('already uses that inventory number')) {
        laptopForm.setFields([{ name: 'inventoryNumber', errors: [error.message] }]);
      } else {
        message.error('Failed to update inventory number: ' + error.message);
      }
    } finally {
      setLaptopSaving(false);
    }
  };

  const handlePasswordChange = async (values) => {
    if (!user?.email) {
      message.error('User email not found. Please log in again.');
      return;
    }

    setLoading(true);
    try {
      await authService.resetPassword(
        user.email,
        values.currentPassword,
        values.newPassword,
        values.confirmPassword
      );

      notification.success({
        message: 'Password Changed Successfully!',
        description: 'Your password has been updated. Please use your new password for future logins.',
        icon: <CheckCircleOutlined style={{ color: '#12805c' }} />,
        duration: 6,
        placement: 'topRight'
      });
      message.success('Password changed successfully!');
      form.resetFields();
    } catch (error) {
      message.error(error.message || 'Failed to change password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || 'Your account';
  const initials = (fullName.match(/\b\w/g) || []).slice(0, 2).join('').toUpperCase();
  const primaryRole = user?.role ?? user?.role_id;
  const extraRoles = (user?.additionalRoles || []).filter((r) => Number(r) !== Number(primaryRole));

  return (
    <DOTheme>
      <Layout className="do-shell do-scope acc-page">
        <NavigationBar />
        <main className="do-main">
          <div className="acc-container">
            <header className="acc-me">
              <span className="acc-avatar" aria-hidden="true">{initials}</span>
              <div className="acc-me__text">
                <h1 className="acc-me__name">{fullName}</h1>
                {user?.email && <p className="acc-me__email">{user.email}</p>}
                {primaryRole != null && (
                  <div className="acc-roles" aria-label="Your roles">
                    <span className="acc-role is-main">{roleName(primaryRole)}</span>
                    {extraRoles.map((r) => <span key={r} className="acc-role">{roleName(r)}</span>)}
                  </div>
                )}
              </div>
            </header>

            <section className="acc-row" aria-labelledby="acc-laptop-title">
              <div className="acc-row__label">
                <h2 id="acc-laptop-title">Your laptop</h2>
                <p>Check the inventory number on the sticker under your laptop.</p>
              </div>
              <div className="acc-row__body">
                {laptopLoading ? (
                  <Spin />
                ) : !laptop ? (
                  <p className="acc-note">{laptopNotice}</p>
                ) : laptop.confirmedAt ? (
                  <div className="acc-confirmed">
                    <span className="acc-confirmed__icon" aria-hidden="true"><LaptopOutlined /></span>
                    <div>
                      <span className="acc-confirmed__number num">{laptop.inventoryNumber}</span>
                      <span className="acc-confirmed__state"><CheckCircleOutlined aria-hidden="true" /> Confirmed</span>
                    </div>
                    <p className="acc-hint">If this is wrong, contact an admin to unlock it for editing.</p>
                  </div>
                ) : (
                  <Form form={laptopForm} layout="vertical" onFinish={handleSaveMyLaptop} className="acc-form">
                    <Form.Item
                      name="inventoryNumber"
                      label="Inventory number"
                      extra="Confirm this matches the label on your laptop, or correct it and save."
                      rules={[{ required: true, message: 'Inventory number is required' }]}
                    >
                      <Input placeholder="Enter inventory number" />
                    </Form.Item>
                    <Button type="primary" htmlType="submit" loading={laptopSaving} icon={<CheckCircleOutlined />}>
                      Save and confirm
                    </Button>
                  </Form>
                )}
              </div>
            </section>

            <section className="acc-row" aria-labelledby="acc-password-title">
              <div className="acc-row__label">
                <h2 id="acc-password-title">Password</h2>
                <p>Use at least 6 characters. You'll use the new password next time you sign in.</p>
              </div>
              <div className="acc-row__body">
                <Form
                  form={form}
                  name="change-password"
                  onFinish={handlePasswordChange}
                  layout="vertical"
                  autoComplete="off"
                  className="acc-form"
                  requiredMark={false}
                >
                  <Form.Item
                    label="Current password"
                    name="currentPassword"
                    rules={[{ required: true, message: 'Please input your current password!' }]}
                  >
                    <Input.Password prefix={<LockOutlined />} placeholder="Enter current password" autoComplete="current-password" />
                  </Form.Item>

                  <Form.Item
                    label="New password"
                    name="newPassword"
                    rules={[
                      { required: true, message: 'Please input your new password!' },
                      { min: 6, message: 'Password must be at least 6 characters long!' },
                    ]}
                  >
                    <Input.Password prefix={<LockOutlined />} placeholder="Enter new password" autoComplete="new-password" />
                  </Form.Item>

                  <Form.Item
                    label="Confirm new password"
                    name="confirmPassword"
                    dependencies={['newPassword']}
                    rules={[
                      { required: true, message: 'Please confirm your new password!' },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue('newPassword') === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error('The two passwords do not match!'));
                        },
                      }),
                    ]}
                  >
                    <Input.Password prefix={<LockOutlined />} placeholder="Confirm new password" autoComplete="new-password" />
                  </Form.Item>

                  <Button type="primary" htmlType="submit" loading={loading} disabled={loading} icon={<LockOutlined />}>
                    {loading ? 'Changing password…' : 'Change password'}
                  </Button>
                </Form>
              </div>
            </section>
          </div>
        </main>
      </Layout>
    </DOTheme>
  );
};

export default MyAccount;
